import { ArrowRight } from 'lucide-react'
import { Link } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { BackgroundAtmosphere } from '@/features/landing/components/BackgroundAtmosphere'
import { HeroPreview } from '@/features/landing/components/HeroPreview'
import { ctaClicked } from '@/lib/analytics'

/**
 * Landing hero — first thing every visitor sees. Two jobs, both ruthless:
 *
 *  1. Answer "what is this?" in the first second. The previous H1
 *     ("The home gaming squads don't have") read as poetry, not product.
 *     The new headline names *what e404 is*; the sub-headline names
 *     *what you do with it*; the visual preview on the right shows what
 *     it looks like. Three reinforcing signals.
 *
 *  2. Look unmistakably premium without leaning on motion. Per the
 *     web-design-guidelines + Emil's frequency framework, the hero is
 *     viewed every visit — no looping animation, no `transition: all`.
 *     The static visual interest comes from the layered preview card,
 *     the tactical corner brackets, and the gradient halo behind the
 *     preview.
 */
export function Hero() {
  return (
    <section className="relative isolate overflow-hidden border-b border-border/60 px-6 pt-20 pb-24 md:pt-24 md:pb-28">
      <BackgroundAtmosphere />

      {/* Tactical corner brackets — the framing motif used throughout the
          app, here treated as the canvas edge of the hero. */}
      <CornerBracket position="tl" />
      <CornerBracket position="tr" />
      <CornerBracket position="bl" />
      <CornerBracket position="br" />

      {/* Tactical readouts. The pre-headline pill identifies WHO the
          product is for so the page identifies its audience the moment
          the visitor's eye lands — even before they read the headline. */}
      <div
        aria-hidden
        className="absolute top-8 left-8 hidden font-display text-[10px] tracking-[0.3em] text-fg-subtle uppercase md:block"
      >
        E404 · YOUR SQUAD'S HOME
      </div>
      <div
        aria-hidden
        className="absolute top-8 right-8 hidden font-display text-[10px] tracking-[0.3em] text-fg-subtle uppercase md:block"
      >
        ALPHA · 6 WEEKS IN
      </div>
      <div
        aria-hidden
        className="absolute bottom-6 left-8 hidden items-center gap-2 font-display text-[10px] tracking-[0.3em] text-fg-subtle uppercase md:flex"
      >
        <span className="inline-flex h-1.5 w-1.5 rounded-full bg-success" />
        Squad's online
      </div>

      <div className="relative mx-auto grid w-full max-w-6xl items-center gap-12 lg:grid-cols-[1.05fr_1fr] lg:gap-16">
        {/* Copy column */}
        <div className="flex flex-col items-center text-center lg:items-start lg:text-left">
          {/* Above-headline pill — identifies the audience instantly. */}
          <div className="mb-7 inline-flex items-center gap-2 rounded-full border border-border bg-surface/80 px-3 py-1 font-display text-xs tracking-wider text-fg-muted uppercase backdrop-blur">
            <span
              aria-hidden
              className="inline-flex h-1.5 w-1.5 rounded-full bg-primary"
            />
            Where your squad already is
          </div>

          {/* Headline — direct. Names what the product IS, not how it
              feels. Per the writing principles: lead with intent, not a
              poetic absence. */}
          <h1 className="font-display text-5xl leading-[1.02] font-bold text-balance md:text-6xl lg:text-7xl">
            Your whole gaming community, in{' '}
            <span className="gradient-text">one place</span>
            <span className="text-fg">.</span>
          </h1>

          {/* Sub — names what you DO with it, leading with the variety
              of event types so visitors clock immediately that this is
              clan management, not just a bracket tool. */}
          <p className="mt-6 max-w-xl text-base text-fg-muted text-pretty md:text-lg">
            Your roster, your events, who&rsquo;s online right now. Session
            tonight, meetup Saturday, a tournament next week &mdash; it all lives
            here, and Discord still gets the ping. e404 is where your clan
            actually runs, instead of five tabs nobody keeps open.
          </p>

          {/* CTAs */}
          <div className="mt-9 flex w-full flex-col items-center gap-3 sm:w-auto sm:flex-row lg:items-start">
            <Button asChild variant="primary" size="lg" className="w-full sm:w-auto">
              <Link
                to="/signup"
                onClick={() => ctaClicked('get_started', 'hero', '/signup')}
              >
                Get started free
                <ArrowRight className="size-4" aria-hidden />
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              size="lg"
              className="font-display w-full sm:w-auto"
            >
              <a href="#how">Show me how it works</a>
            </Button>
          </div>

          {/* Trust strip — answers the implicit "what's the catch?". Tiny,
              quiet, but resolves an objection the CTA pair otherwise leaves
              open. */}
          <p className="font-display mt-6 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[10px] tracking-[0.2em] text-fg-subtle uppercase lg:justify-start">
            <span>Free up to 10 members</span>
            <span aria-hidden className="text-fg-subtle/60">·</span>
            <span>No card required</span>
            <span aria-hidden className="text-fg-subtle/60">·</span>
            <span>Web + Android</span>
          </p>
        </div>

        {/* Preview column — the visual that answers "what does it look
            like?". Stacks under the copy on smaller viewports so it stays
            within the fold on lg+ where it has space to breathe. */}
        <div className="relative flex items-center justify-center">
          <HeroPreview />
        </div>
      </div>
    </section>
  )
}

/**
 * Decorative L-shaped bracket pinned to a hero corner. Tactical-frame
 * detailing — small but consistent across the four corners.
 */
function CornerBracket({ position }: { position: 'tl' | 'tr' | 'bl' | 'br' }) {
  const positions = {
    tl: 'top-4 left-4 border-t border-l',
    tr: 'top-4 right-4 border-t border-r',
    bl: 'bottom-4 left-4 border-b border-l',
    br: 'bottom-4 right-4 border-b border-r',
  } as const

  return (
    <span
      aria-hidden
      className={`pointer-events-none absolute h-6 w-6 border-fg-subtle/40 ${positions[position]}`}
    />
  )
}
